import React, { ComponentType, useCallback } from 'react';
import {
  ArrayLayoutProps,
  RankedTester,
  rankWith,
  isObjectArray,
  or,
  isPrimitiveArrayControl,
  isObjectArrayControl,
  OwnPropsOfControl,
  JsonFormsState,
  StatePropsOfArrayLayout,
  mapStateToControlWithDetailProps,
  Resolve,
  getTranslator,
  getCombinedErrorMessage,
  getSubErrorsAt,
  getErrorTranslator,
  getArrayTranslations,
  arrayDefaultTranslations,
} from '@jsonforms/core';
import { MantineArrayLayout } from './MantineArrayLayout';
import { ctxDispatchToArrayControlProps, JsonFormsStateContext, withJsonFormsContext } from '@jsonforms/react';
import { useNavigationContext } from '../../contexts/NavigationContext';

// ! This part of code is copied from jsonform/core and jsonform/react packages and modified to add maxItems prop to ArrayLayoutProps
// idk why it's not included in the original package despite that minItems is included

export const mapStateToArrayLayoutProps = (
  state: JsonFormsState,
  ownProps: OwnPropsOfControl
): StatePropsOfArrayLayout & { maxItems?: number } => {
  const { path, schema, uischema, errors, i18nKeyPrefix, label, ...props } = mapStateToControlWithDetailProps(
    state,
    ownProps
  );

  const resolvedSchema = Resolve.schema(schema, 'items', props.rootSchema);
  const t = getTranslator()(state);
  // TODO Does not consider 'i18n' keys which are specified in the ui schemas of the sub errors
  const childErrors = getCombinedErrorMessage(
    getSubErrorsAt(path, resolvedSchema)(state),
    getErrorTranslator()(state),
    t,
    undefined,
    undefined,
    undefined
  );

  const allErrors = errors + (errors.length > 0 && childErrors.length > 0 ? '\n' : '') + childErrors;
  return {
    ...props,
    label,
    path,
    uischema,
    schema: resolvedSchema,
    data: props.data ? props.data.length : 0,
    errors: allErrors,
    minItems: schema.minItems,
    maxItems: schema.maxItems,
    translations: getArrayTranslations(t, arrayDefaultTranslations, i18nKeyPrefix!, label),
  };
};

export const ctxToArrayLayoutProps = (ctx: JsonFormsStateContext, props: OwnPropsOfControl) =>
  mapStateToArrayLayoutProps({ jsonforms: { ...ctx } }, props);

const withContextToArrayLayoutProps = (Component: ComponentType<ArrayLayoutProps>) =>
  function WithContextToArrayLayoutProps({ ctx, props }: JsonFormsStateContext & ArrayLayoutProps) {
    const arrayLayoutProps = ctxToArrayLayoutProps(ctx, props);
    const dispatchProps = ctxDispatchToArrayControlProps(ctx.dispatch);
    return <Component {...props} {...arrayLayoutProps} {...dispatchProps} />;
  };

export const withJsonFormsArrayLayoutProps = (
  Component: ComponentType<ArrayLayoutProps>,
  memoize = true
): ComponentType<OwnPropsOfControl> =>
  withJsonFormsContext(withContextToArrayLayoutProps(memoize ? React.memo(Component) : Component));

// ! End of modified code
// --------

export const MantineArrayLayoutRenderer = ({ visible, addItem, ...props }: ArrayLayoutProps) => {
  const { selectedTopNavigation } = useNavigationContext();

  const addItemCb = useCallback((p: string, value: any) => addItem(p, value), [addItem]);
  const isFirstLevel = (props.path?.split('.').length ?? 1) === 1;

  if (!visible) {
    return null;
  }

  if (isFirstLevel && selectedTopNavigation !== props.path) {
    return null;
  }

  return <MantineArrayLayout visible={visible} addItem={addItemCb} {...props} />;
};

export const mantineArrayLayoutTester: RankedTester = rankWith(
  5,
  or(isObjectArray, isObjectArrayControl, isPrimitiveArrayControl)
);
export default withJsonFormsArrayLayoutProps(MantineArrayLayoutRenderer);
